/**
 * InterPlay ACM audio, decoded in TypeScript — the Mod Maker's sound preview.
 *
 * A line-faithful port of `engine/src/sound_decoder.cc` (CE's cleaned-up
 * rendition of the original AudioDecoder): 24-bit magic 0x32897, a subband
 * count and per-band bit formats, a shared ±scale table, and the in-place
 * "untransform" that folds subbands back into PCM. The port keeps the C
 * code's exact integer behaviour on purpose:
 *
 *   - every arithmetic step runs through JS bitwise ops (`| 0`, `>>`), which
 *     are int32 with arithmetic right shift — the same as the engine build;
 *   - the two `a4 == 4` branches reproduce the C pointer arithmetic verbatim,
 *     including the surprising `v26 + 4` (four *ints*) — that is what ships;
 *   - byte-level buffers (`prev`) go through a DataView because the C reads
 *     them as a mix of int32 and int16 at 2-byte offsets.
 *
 * Input is the whole file (sfx are a few tens of KB); when the bit reader
 * runs past the end it feeds zeroes, exactly like ByteReaderFill.
 *
 * Nothing here throws for damaged data — `decodeAcm` returns null for
 * anything that fails the header check, and the caller treats "cannot decode"
 * the same as "file not there": no preview button.
 */

export interface DecodedAcm {
  channels: number;
  rate: number;
  /** Mono/interleaved 16-bit samples, `sampleCount` long. */
  samples: Int16Array;
}

class Bits {
  private readonly data: Uint8Array;
  private pos = 0;
  hold = 0;
  bits = 0;

  constructor(data: Uint8Array) {
    this.data = data;
  }

  require(bits: number): void {
    while (this.bits < bits) {
      // Past the end the C reader refills with zeroes and keeps going.
      const ch = this.pos < this.data.length ? this.data[this.pos++]! : 0;
      this.hold = (this.hold | (ch << this.bits)) | 0;
      this.bits += 8;
    }
  }

  drop(bits: number): void {
    this.hold >>= bits;
    this.bits -= bits;
  }

  /** require + mask + drop, the common read shape. */
  read(bits: number): number {
    this.require(bits);
    const value = this.hold & ((1 << bits) - 1);
    this.drop(bits);
    return value;
  }
}

// The ±scale table: 0x8000 signed shorts each side of the midpoint.
const SCALE_MID = 0x8000;

// pack tables (init_pack_tables)
const pack3_3 = new Uint8Array(32);
const pack5_3 = new Uint16Array(128);
const pack11_2 = new Uint8Array(128);
{
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++) for (let m = 0; m < 3; m++) pack3_3[i + j * 3 + m * 9] = i + j * 4 + m * 16;
  for (let i = 0; i < 5; i++)
    for (let j = 0; j < 5; j++) for (let m = 0; m < 5; m++) pack5_3[i + j * 5 + m * 25] = i + j * 8 + m * 64;
  for (let i = 0; i < 11; i++) for (let j = 0; j < 11; j++) pack11_2[i + j * 11] = i + j * 16;
}

interface Ctx {
  br: Bits;
  scale: Int16Array;
  samples: Int32Array;
  samplesPerSubband: number;
  subbands: number;
  totalSamples: number;
  levels: number;
}

type BandFn = (ctx: Ctx, offset: number, bits: number) => number;

const ReadBand_Fail: BandFn = () => 0;

const ReadBand_Fmt0: BandFn = (ctx, offset) => {
  let p = offset;
  for (let i = ctx.samplesPerSubband; i !== 0; i--) {
    ctx.samples[p] = 0;
    p += ctx.subbands;
  }
  return 1;
};

const ReadBand_Fmt3_16: BandFn = (ctx, offset, bits) => {
  // base = scale0 - 2^(bits-1) shorts ((int)(UINT_MAX << (bits - 1)))
  const base = SCALE_MID - (1 << (bits - 1));
  const mask = (1 << bits) - 1;
  let p = offset;
  for (let i = ctx.samplesPerSubband; i !== 0; i--) {
    ctx.br.require(bits);
    const value = ctx.br.hold;
    ctx.br.drop(bits);
    ctx.samples[p] = ctx.scale[base + (mask & value)]!;
    p += ctx.subbands;
  }
  return 1;
};

const ReadBand_Fmt17: BandFn = (ctx, offset) => {
  const { br, scale, samples, subbands } = ctx;
  let p = offset;
  let i = ctx.samplesPerSubband;
  while (i !== 0) {
    br.require(3);
    const value = br.hold & 0xff;
    if (!(value & 0x01)) {
      br.drop(1);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
    } else if (!(value & 0x02)) {
      br.drop(2);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
    } else {
      br.drop(3);
      samples[p] = value & 0x04 ? scale[SCALE_MID + 1]! : scale[SCALE_MID - 1]!;
      p += subbands;
      i--;
    }
  }
  return 1;
};

const ReadBand_Fmt18: BandFn = (ctx, offset) => {
  const { br, scale, samples, subbands } = ctx;
  let p = offset;
  let i = ctx.samplesPerSubband;
  while (i !== 0) {
    br.require(2);
    const value = br.hold;
    if (!(value & 0x01)) {
      br.drop(1);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) return 1;
    } else {
      br.drop(2);
      samples[p] = value & 0x02 ? scale[SCALE_MID + 1]! : scale[SCALE_MID - 1]!;
      p += subbands;
      i--;
    }
  }
  return 1;
};

const ReadBand_Fmt19: BandFn = (ctx, offset) => {
  const { br, scale, samples, subbands } = ctx;
  const base = SCALE_MID - 1;
  let p = offset;
  let i = ctx.samplesPerSubband;
  while (i !== 0) {
    let value = br.read(5) & 0x1f;
    value = pack3_3[value]!;
    samples[p] = scale[base + (value & 0x03)]!;
    p += subbands;
    if (--i === 0) break;
    samples[p] = scale[base + ((value >> 2) & 0x03)]!;
    p += subbands;
    if (--i === 0) break;
    samples[p] = scale[base + (value >> 4)]!;
    p += subbands;
    i--;
  }
  return 1;
};

const ReadBand_Fmt20: BandFn = (ctx, offset) => {
  const { br, scale, samples, subbands } = ctx;
  let p = offset;
  let i = ctx.samplesPerSubband;
  while (i !== 0) {
    br.require(4);
    const value = br.hold & 0xff;
    if (!(value & 0x01)) {
      br.drop(1);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
    } else if (!(value & 0x02)) {
      br.drop(2);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
    } else {
      br.drop(4);
      const idx = value & 0x08 ? (value & 0x04 ? 2 : 1) : value & 0x04 ? -1 : -2;
      samples[p] = scale[SCALE_MID + idx]!;
      p += subbands;
      i--;
    }
  }
  return 1;
};

const ReadBand_Fmt21: BandFn = (ctx, offset) => {
  const { br, scale, samples, subbands } = ctx;
  let p = offset;
  let i = ctx.samplesPerSubband;
  while (i !== 0) {
    br.require(3);
    const value = br.hold & 0xff;
    if (!(value & 0x01)) {
      br.drop(1);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
    } else {
      br.drop(3);
      const idx = value & 0x04 ? (value & 0x02 ? 2 : 1) : value & 0x02 ? -1 : -2;
      samples[p] = scale[SCALE_MID + idx]!;
      p += subbands;
      i--;
    }
  }
  return 1;
};

const ReadBand_Fmt22: BandFn = (ctx, offset) => {
  const { br, scale, samples, subbands } = ctx;
  const base = SCALE_MID - 2;
  let p = offset;
  let i = ctx.samplesPerSubband;
  while (i !== 0) {
    let value = br.read(7) & 0x7f;
    value = pack5_3[value]!;
    samples[p] = scale[base + (value & 7)]!;
    p += subbands;
    if (--i === 0) break;
    samples[p] = scale[base + ((value >> 3) & 7)]!;
    p += subbands;
    if (--i === 0) break;
    samples[p] = scale[base + (value >> 6)]!;
    p += subbands;
    if (--i === 0) break;
  }
  return 1;
};

const ReadBand_Fmt23: BandFn = (ctx, offset) => {
  const { br, scale, samples, subbands } = ctx;
  let p = offset;
  let i = ctx.samplesPerSubband;
  while (i !== 0) {
    br.require(5);
    let value = br.hold;
    if (!(value & 0x01)) {
      br.drop(1);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
    } else if (!(value & 0x02)) {
      br.drop(2);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
    } else if (!(value & 0x04)) {
      br.drop(4);
      samples[p] = value & 0x08 ? scale[SCALE_MID + 1]! : scale[SCALE_MID - 1]!;
      p += subbands;
      if (--i === 0) break;
    } else {
      br.drop(5);
      value = (value >> 3) & 0x03;
      if (value >= 2) value += 3;
      samples[p] = scale[SCALE_MID + value - 3]!;
      p += subbands;
      i--;
    }
  }
  return 1;
};

const ReadBand_Fmt24: BandFn = (ctx, offset) => {
  const { br, scale, samples, subbands } = ctx;
  let p = offset;
  let i = ctx.samplesPerSubband;
  while (i !== 0) {
    br.require(4);
    let value = br.hold & 0xff;
    if (!(value & 0x01)) {
      br.drop(1);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
    } else if (!(value & 0x02)) {
      br.drop(3);
      samples[p] = value & 0x04 ? scale[SCALE_MID + 1]! : scale[SCALE_MID - 1]!;
      p += subbands;
      if (--i === 0) break;
    } else {
      br.drop(4);
      value = (value >> 2) & 0x03;
      if (value >= 2) value += 3;
      samples[p] = scale[SCALE_MID + value - 3]!;
      p += subbands;
      i--;
    }
  }
  return 1;
};

const ReadBand_Fmt26: BandFn = (ctx, offset) => {
  const { br, scale, samples, subbands } = ctx;
  let p = offset;
  let i = ctx.samplesPerSubband;
  while (i !== 0) {
    br.require(5);
    let value = br.hold;
    if (!(value & 0x01)) {
      br.drop(1);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
    } else if (!(value & 0x02)) {
      br.drop(2);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
    } else {
      br.drop(5);
      value = (value >> 2) & 0x07;
      if (value >= 4) value += 1;
      samples[p] = scale[SCALE_MID + value - 4]!;
      p += subbands;
      i--;
    }
  }
  return 1;
};

const ReadBand_Fmt27: BandFn = (ctx, offset) => {
  const { br, scale, samples, subbands } = ctx;
  let p = offset;
  let i = ctx.samplesPerSubband;
  while (i !== 0) {
    br.require(4);
    let value = br.hold;
    if (!(value & 0x01)) {
      br.drop(1);
      samples[p] = 0;
      p += subbands;
      if (--i === 0) break;
    } else {
      br.drop(4);
      value = (value >> 1) & 0x07;
      if (value >= 4) value += 1;
      samples[p] = scale[SCALE_MID + value - 4]!;
      p += subbands;
      i--;
    }
  }
  return 1;
};

const ReadBand_Fmt29: BandFn = (ctx, offset) => {
  const { br, scale, samples, subbands } = ctx;
  let p = offset;
  let i = ctx.samplesPerSubband;
  while (i !== 0) {
    let value = br.read(7) & 0x7f;
    value = pack11_2[value]!;
    samples[p] = scale[SCALE_MID + (value & 0x0f) - 5]!;
    p += subbands;
    if (--i === 0) break;
    samples[p] = scale[SCALE_MID + (value >> 4) - 5]!;
    p += subbands;
    if (--i === 0) break;
  }
  return 1;
};

const ReadBand_Fmt31: BandFn = (ctx) => {
  const { br, samples } = ctx;
  for (let n = 0; n < ctx.totalSamples; n++) {
    const value = br.read(16);
    samples[n] = ((value << 16) >> (16 - ctx.levels)) | 0;
  }
  return 0;
};

const BAND_TABLE: BandFn[] = [
  ReadBand_Fmt0, ReadBand_Fail, ReadBand_Fail,
  ReadBand_Fmt3_16, ReadBand_Fmt3_16, ReadBand_Fmt3_16, ReadBand_Fmt3_16, ReadBand_Fmt3_16,
  ReadBand_Fmt3_16, ReadBand_Fmt3_16, ReadBand_Fmt3_16, ReadBand_Fmt3_16, ReadBand_Fmt3_16,
  ReadBand_Fmt3_16, ReadBand_Fmt3_16, ReadBand_Fmt3_16, ReadBand_Fmt3_16,
  ReadBand_Fmt17, ReadBand_Fmt18, ReadBand_Fmt19, ReadBand_Fmt20, ReadBand_Fmt21, ReadBand_Fmt22,
  ReadBand_Fmt23, ReadBand_Fmt24, ReadBand_Fail, ReadBand_Fmt26, ReadBand_Fmt27, ReadBand_Fail,
  ReadBand_Fmt29, ReadBand_Fail, ReadBand_Fmt31,
];

function readBands(ctx: Ctx): boolean {
  const magnitude = ctx.br.read(4);
  const step = ctx.br.read(16);

  // Rebuild the ±scale ramp around the midpoint.
  const count = 1 << magnitude;
  let v = 0;
  for (let i = 0; i < count; i++) {
    // Stored as unsigned short in C; the Int16Array coerces identically.
    ctx.scale[SCALE_MID + i] = v & 0xffff;
    v = (v + step) | 0;
  }
  v = -step | 0;
  for (let i = 1; i <= count; i++) {
    ctx.scale[SCALE_MID - i] = v & 0xffff;
    v = (v - step) | 0;
  }

  for (let index = 0; index < ctx.subbands; index++) {
    const bits = ctx.br.read(5);
    const fn = BAND_TABLE[bits]!;
    if (!fn(ctx, index, bits)) return false;
  }
  return true;
}

/**
 * untransform_subband0, byte-faithful. `prev` is the DataView over the
 * prev-samples scratch, `prevAt` the running byte offset (C's `a1`);
 * `samples`/`at` is the int-indexed samples buffer (C's `a2`).
 */
function untransformSubband0(prev: DataView, prevAt: number, samples: Int32Array, at: number, a3: number, a4: number): void {
  if (a4 === 2) return; // the C branch counts a3 down and does nothing
  if (a4 === 4) {
    let v9 = at + a3;
    let v10 = at + a3 * 3;
    let v11 = at + a3 * 2;
    let a1 = prevAt;
    let a2 = at;
    for (let n = a3; n !== 0; n--) {
      const v12 = prev.getInt32(a1, true) >> 16;
      const v13 = samples[a2]!;
      samples[a2] = (prev.getInt16(a1, true) + 2 * v12 + v13) | 0;
      const v14 = samples[v9]!;
      samples[v9] = (2 * v13 - v12 - v14) | 0;
      const v15 = samples[v11]!;
      samples[v11] = (2 * v14 + v15 + v13) | 0;
      const v16 = samples[v10]!;
      samples[v10] = (2 * v15 - v14 - v16) | 0;
      v10++;
      v11++;
      v9++;
      prev.setInt16(a1, v15 & 0xffff, true);
      prev.setInt16(a1 + 2, v16 & 0xffff, true);
      a1 += 4;
      a2++;
    }
    return;
  }

  const v30 = a4 >> 1;
  let a1 = prevAt;
  let a2 = at;
  for (let n = a3; n !== 0; n--) {
    let v19 = a2;
    let v20 = 0;
    let v22 = 0;
    if (!(v30 & 0x01)) {
      v20 = prev.getInt16(a1, true);
      v22 = prev.getInt32(a1, true) >> 16;
    }
    for (let v23 = (v30 >> 1) - 1; v23 !== -1; v23--) {
      const v24 = samples[v19]!;
      samples[v19] = (samples[v19]! + 2 * v22 + v20) | 0;
      v19 += a3;
      const v26 = samples[v19]!;
      samples[v19] = (2 * v24 - v22 - v26) | 0;
      v19 += a3;
      v20 = samples[v19]!;
      samples[v19] = (samples[v19]! + 2 * v26 + v24) | 0;
      v19 += a3;
      v22 = samples[v19]!;
      samples[v19] = (2 * v20 - v26 - v22) | 0;
      v19 += a3;
    }
    prev.setInt16(a1, v20 & 0xffff, true);
    prev.setInt16(a1 + 2, v22 & 0xffff, true);
    a1 += 4;
    a2++;
  }
}

/** untransform_subband. `prevAt` is C's `a1` in bytes; `at` C's `a2` in ints. */
function untransformSubband(prev: DataView, prevAt: number, samples: Int32Array, at: number, a3: number, a4: number): void {
  if (a4 === 4) {
    // Verbatim port of the shipped pointer arithmetic: v26 is an int*, and
    // the code reads/writes *(v26 + 4) — four ints (16 bytes) ahead — while
    // advancing v26 by two ints per column. Do not "fix" this to v26[1].
    let v26 = prevAt; // bytes
    let v25 = at;
    // In the C these are byte pointers off a2: v4 = a2 + 4*a3 bytes (= a3
    // ints), v5 = a2 + 3*a3 bytes, v6 = a2 + 2*a3 bytes. This branch is only
    // reached as the FIRST subband level (a4 == 4 happens when the final
    // block has one sample row), where a3 = subbands/4 — a multiple of 4 for
    // every level count the format produces, so the byte offsets are
    // int-aligned and expressible as int indices.
    let v4 = at + a3;
    let v5 = at + ((3 * a3) >> 2);
    let v6 = at + ((2 * a3) >> 2);
    let n = a3;
    while (n--) {
      const v7 = prev.getInt32(v26 + 16, true);
      const v8 = samples[v25]!;
      samples[v25] = (prev.getInt32(v26, true) + 2 * v7) | 0;
      const v9 = samples[v4]!;
      samples[v4] = (2 * v8 - v7 - v9) | 0;
      const v10 = samples[v6]!;
      samples[v6] = (samples[v6]! + 2 * v9 + v8) | 0;
      const v11 = samples[v5]!;
      samples[v5] = (2 * v10 - v9 - v11) | 0;
      v5++;
      v6++;
      v4++;
      prev.setInt32(v26, v10 | 0, true);
      prev.setInt32(v26 + 16, v11 | 0, true);
      v26 += 8;
      v25 += 1;
    }
    return;
  }

  let v26 = prevAt; // bytes; reads two consecutive ints per column
  let v25 = at;
  for (let n = a3; n !== 0; n--) {
    let v14 = v25;
    let v15 = prev.getInt32(v26, true);
    let v16 = prev.getInt32(v26 + 4, true);
    for (let v13 = (a4 >> 2) - 1; v13 !== -1; v13--) {
      const v17 = samples[v14]!;
      samples[v14] = (samples[v14]! + 2 * v16 + v15) | 0;
      let v18 = v14 + a3;
      const v19 = samples[v18]!;
      samples[v18] = (2 * v17 - v16 - v19) | 0;
      const v20 = v18 + a3;
      v15 = samples[v20]!;
      samples[v20] = (samples[v20]! + 2 * v19 + v17) | 0;
      const v21 = v20 + a3;
      v16 = samples[v21]!;
      samples[v21] = (2 * v15 - v19 - v16) | 0;
      v14 = v21 + a3;
    }
    prev.setInt32(v26, v15 | 0, true);
    prev.setInt32(v26 + 4, v16 | 0, true);
    v26 += 8;
    v25 += 1;
  }
}

/** Decodes a whole .ACM. Returns null when the file is not one. */
export function decodeAcm(bytes: Uint8Array): DecodedAcm | null {
  const br = new Bits(bytes);

  if (br.read(24) !== 0x32897) return null;
  if (br.read(8) !== 1) return null;

  let fileCnt = br.read(16);
  fileCnt |= br.read(16) << 16;
  const channels = br.read(16);
  const rate = br.read(16);
  const levels = br.read(4);

  const subbands = 1 << levels;
  br.require(12);
  const samplesPerSubband = br.hold & 0x0fff;
  br.drop(12);
  const totalSamples = samplesPerSubband * subbands;

  if (fileCnt <= 0 || totalSamples <= 0 || channels < 1 || rate === 0) return null;
  // A corrupt header could claim absurd sizes; 1<<24 samples is ~10 minutes.
  if (fileCnt > 1 << 24 || totalSamples > 1 << 22) return null;

  const prevInts = levels !== 0 ? (3 * subbands) / 2 - 2 : 0;
  // C allocates sizeof(char*) per element — 8 bytes on the engine's own
  // wasm32? No: wasm32 pointers are 4 bytes, so it is 4 bytes per element.
  // Give 8 to be safe against the byte-offset walk; zero-filled like calloc.
  const prev = new DataView(new ArrayBuffer(Math.max(prevInts * 8, 8)));
  const samples = new Int32Array(totalSamples * 2);

  let blockSamplesPerSubband = Math.floor(2048 / subbands) - 2;
  if (blockSamplesPerSubband < 1) blockSamplesPerSubband = 1;
  const blockTotalSamples = blockSamplesPerSubband * subbands;

  const ctx: Ctx = {
    br,
    scale: new Int16Array(0x10000),
    samples,
    samplesPerSubband,
    subbands,
    totalSamples,
    levels,
  };

  const untransformAll = (): void => {
    if (!levels) return;
    let ptr = 0; // int index into samples
    let v8 = samplesPerSubband;
    while (v8 > 0) {
      let v3 = subbands >> 1;
      let v4 = Math.min(blockSamplesPerSubband, v8) * 2;
      untransformSubband0(prev, 0, samples, ptr, v3, v4);
      let v5 = ptr;
      for (let v6 = 0; v6 < v4; v6++) {
        samples[v5] = (samples[v5]! + 1) | 0;
        v5 += v3;
      }
      let j = 4 * v3; // byte offset into prev
      for (;;) {
        v3 >>= 1;
        v4 *= 2;
        if (v3 === 0) break;
        untransformSubband(prev, j, samples, ptr, v3, v4);
        j += 8 * v3;
      }
      ptr += blockTotalSamples;
      v8 -= blockSamplesPerSubband;
    }
  };

  const out = new Int16Array(fileCnt);
  let written = 0;
  let remaining = fileCnt;
  while (remaining > 0) {
    // Like soundDecoderFill: a false ReadBands (Fmt31) skips the transform
    // but the block is still served — that handler wrote PCM directly.
    if (readBands(ctx)) untransformAll();
    const chunk = Math.min(totalSamples, remaining);
    for (let n = 0; n < chunk; n++) {
      out[written++] = (samples[n]! >> levels) & 0xffff;
    }
    remaining -= chunk;
  }

  return { channels, rate, samples: written === fileCnt ? out : out.subarray(0, written) as Int16Array };
}
